import React, { Fragment, useState, useEffect, useRef } from 'react'
import { renderSortingIcon } from '../Templates/functions'
import { LightTooltip } from '../Templates/icons.js'

const FormColumn = (props) => {
    const [inputValue, setInputValue] = useState(props.content[0]);
    const [defaultInputValue, setDefaultInputValue] = useState(props.content[0]);
    const inputRef = useRef();
    const instanceIdRef = useRef(Math.random().toString(36).slice(2));

    // Functions to handle input focus for WI title
    useEffect(() => {
        console.log(`[FormColumn ${instanceIdRef.current}] MOUNT with title=`, props.title, 'content[0]=', props.content?.[0]);
        if (inputRef.current && title === 'Work Instruction Title') {
            console.log(`[FormColumn] Component mounted for WI Title, focusing input`);
            console.log(`[FormColumn] Current activeElement before initial focus:`, document.activeElement);
            
            // Check if PublicationPage protection system is active
            const isPublicationModalActive = window._publicationModalRegistry && window._publicationModalRegistry.size > 0;
            console.log(`[FormColumn] PublicationPage protection active:`, isPublicationModalActive);
            
            // Add detailed event listeners for debugging
            const input = inputRef.current;
            
            const handleFocusEvent = (e) => {
                console.log(`[FormColumn] FOCUS event triggered on WI Title input, activeElement:`, document.activeElement);
                console.log(`[FormColumn] Focus event details:`, {
                    type: e.type,
                    target: e.target,
                    relatedTarget: e.relatedTarget,
                    timeStamp: e.timeStamp
                });
            };
            
            const handleBlurEvent = (e) => {
                console.log(`[FormColumn] BLUR event triggered on WI Title input, activeElement:`, document.activeElement);
                console.log(`[FormColumn] Blur event details:`, {
                    type: e.type,
                    target: e.target,
                    relatedTarget: e.relatedTarget,
                    timeStamp: e.timeStamp
                });
            };
            
            input.addEventListener('focus', handleFocusEvent);
            input.addEventListener('blur', handleBlurEvent);
            
            // Check if we're in a nested WI (not the first tab)
            const activeTabPanel = document.querySelector('.react-tabs__tab-panel--selected');
            const isNestedWI = activeTabPanel && activeTabPanel.id !== 'react-tabs-1';
            console.log(`[FormColumn] Is nested WI:`, isNestedWI, 'Active tab panel:', activeTabPanel?.id);
            
            // For nested WIs, always auto-focus regardless of protection
            // For main WI, only auto-focus if PublicationPage protection is NOT active
            if (isNestedWI) {
                console.log(`[FormColumn] Nested WI detected - auto-focusing input regardless of protection`);
                input.focus();
                console.log(`[FormColumn] Nested WI focus complete, new activeElement:`, document.activeElement);
            } else if (!isPublicationModalActive) {
                console.log(`[FormColumn] Main WI - auto-focusing input (no publication protection active)`);
                input.focus();
                console.log(`[FormColumn] Main WI focus complete, new activeElement:`, document.activeElement);
            } else {
                console.log(`[FormColumn] Main WI - skipping auto-focus, PublicationPage protection will handle focus`);
            }
            
            // Cleanup function
            return () => {
                if (input) {
                    input.removeEventListener('focus', handleFocusEvent);
                    input.removeEventListener('blur', handleBlurEvent);
                    console.log(`[FormColumn] Cleaned up WI Title input event listeners`);
                }
                console.log(`[FormColumn ${instanceIdRef.current}] UNMOUNT (title=`, props.title, ')');
            };
        }
        return () => {
            console.log(`[FormColumn ${instanceIdRef.current}] UNMOUNT (no WI title input)`);
        }
    }, []); // Empty dependency array - only run on mount, not on inputValue changes

    useEffect(() => {
        // Log on prop changes that can trigger re-renders
        console.log(`[FormColumn ${instanceIdRef.current}] RE-RENDER due to prop change: title=`, props.title, 'content[0]=', props.content?.[0]);
    }, [props.title, props.content]);

    const handleInputChangeAndHandleCursorPosition = (event) => {
        const cursorPosition = event.target.selectionStart;
        if (title === 'Work Instruction Title') {
            console.log(`[FormColumn] Input change - oldValue:`, inputValue, 'newValue:', event.target.value);
            console.log(`[FormColumn] Cursor position:`, cursorPosition, 'activeElement:', document.activeElement);
        }
        setInputValue(event.target.value);
        
        // Preserve focus after React re-render
        if (title === 'Work Instruction Title') {
            setTimeout(() => {
                console.log(`[FormColumn] Checking focus after re-render...`);
                console.log(`[FormColumn] Current activeElement:`, document.activeElement);
                console.log(`[FormColumn] inputRef.current:`, inputRef.current);
                console.log(`[FormColumn] inputRef.current exists:`, !!inputRef.current);
                console.log(`[FormColumn] Are they equal:`, document.activeElement === inputRef.current);
                console.log(`[FormColumn] PublicationPage protection active:`, window._publicationModalRegistry?.size || 0);
                
                if (inputRef.current && document.activeElement !== inputRef.current) {
                    console.log(`[FormColumn] Restoring focus after re-render, activeElement was:`, document.activeElement);
                    
                    // Check if PublicationPage protection might interfere
                    const hasProtection = window._publicationModalRegistry && window._publicationModalRegistry.size > 0;
                    if (hasProtection) {
                        console.log(`[FormColumn] PublicationPage protection detected - this might interfere with focus restoration`);
                        console.log(`[FormColumn] Active protection instances:`, window._publicationModalRegistry.size);
                    }
                    
                    inputRef.current.focus();
                    inputRef.current.selectionStart = cursorPosition;
                    inputRef.current.selectionEnd = cursorPosition;
                    console.log(`[FormColumn] Focus and cursor position restored, new activeElement:`, document.activeElement);
                } else {
                    console.log(`[FormColumn] No focus restoration needed - input already focused or doesn't exist`);
                }
            }, 0);
        }
        //props.setEditedWITitle(inputValue);
    };

    const handleInputBlur = (e) => {
       if (title === 'Work Instruction Title') {
           console.log(`[FormColumn] WI Title blur handler called, inputValue:`, inputValue);
           console.log(`[FormColumn] Blur relatedTarget:`, e?.relatedTarget);
           
           // If focus is trying to move to a tab header, immediately refocus this input and ignore blur
           if (e?.relatedTarget && e.relatedTarget.classList && e.relatedTarget.classList.contains('react-tabs__tab')) {
               console.log('[FormColumn] Blur moved focus to tab header - refocusing WI Title and skipping blur commit');
               setTimeout(() => {
                   if (inputRef.current) {
                       inputRef.current.focus();
                   }
               }, 0);
               return;
           }

           // Don't process blur if it's due to focus protection (null relatedTarget)
           // This prevents value reversion when protection system refocuses
           if (e?.relatedTarget === null) {
               console.log(`[FormColumn] Skipping blur handler - likely due to DOM remount/focus protection`);
               return;
           }
           
           console.log(`[FormColumn] About to call setEditedWITitle`);
       }
       let changed = props.setEditedWITitle(inputValue);
       if (title === 'Work Instruction Title') {
           console.log(`[FormColumn] setEditedWITitle returned:`, changed);
       }
       if(changed){
        setDefaultInputValue(inputValue);
       }
       else{
        if (title === 'Work Instruction Title') {
            console.log(`[FormColumn] Reverting inputValue from`, inputValue, 'to', defaultInputValue);
        }
        setInputValue(defaultInputValue);
       }
    }

    const { numColumns, nullSort, sort, sorting, title, columnWidth, nullHeader, ellipsisAlt, classNames } = props
    const content = props.content.map((item, index) => {
        if (props.fragments) {
            if(title=== 'Work Instruction Title'){
                return <Fragment className='title-format' key={`form-column ${title} ${item} ${Math.random()}`}>{item}<p>Edit</p></Fragment>
            }else{
                return <Fragment key={`form-column ${title} ${item} ${Math.random()}`}>{item}</Fragment>
            }
        }

        let textContent = <div ref={React.createRef()} className={` ${classNames} ${ellipsisAlt?"ellipsisAlt":"ellipsis"}`} style={{cursor: "default"}}>{item}</div>

        
        if(title === 'Work Instruction Title'){
            textContent = 
                <div className='title-format' style={{cursor: "default", display: 'flex', justifyContent: 'space-between', width:'100%'}}>
                    <input 
                        id="wi-title-input"
                        ref={inputRef}
                        value={inputValue} 
                        onChange={handleInputChangeAndHandleCursorPosition}
                        onBlur={handleInputBlur}
                        onFocus={(e) => {
                            if (title === 'Work Instruction Title') {
                                console.log(`[FormColumn] Input focused, preventing React interference`);
                                // Store the current value and remove the value prop temporarily
                                // This prevents React re-renders from interfering with focus
                                e.target._savedValue = e.target.value;
                            }
                        }}
                        style={{border: 'none', background: 'transparent', flexBasis: '0', flexGrow: '1', flexShrink: '1', maxWidth: 'calc(100% - 60px)'}}
                    />
                    <p className='edit-title' style={{marginRight: '2%', flexBasis: 'auto', flexGrow: '0', flexShrink: '0', minWidth: '50px'}}>(Edit)</p>
                </div>
        }



        // let disableTooltip = false
        // if (textContent.ref.current && textContent.ref.current.scrollWidth > textContent.ref.current.clientWidth) {
        //     disableTooltip = false;
        // }

        console.log(`[FormColumn ${instanceIdRef.current}] Rendering list item for title=`, title, 'item=', item);
        return <li id={`form-column-${title}-item`} key={`form-column ${title} ${item} ${Math.random()}`}>
                <LightTooltip title={item} aria-label={item} placement="top" enterDelay={1000} enterNextDelay={1000}>
                    {textContent}
                </LightTooltip>
            </li>
    })

    const width = columnWidth ? columnWidth : `${(100 / numColumns)}%`
 
        return <div id="column-container" style={{ width }}> 
                    {/* If nullHeader={false} is true, then null, else return <div id="column-header"> */}
                    { nullHeader ? null :
                        <h3 id="column-header" className="flex-centered FontSize18" onClick={sort}>
                            <p>{title}</p>
                            {!nullSort ? renderSortingIcon(sorting) : null}
                        </h3> 
                    }
                    <ul id="form-columns-list" className={`flex max-width FontSize16 ${classNames}`}>
                        {content}
                    </ul>
                </div>
}

export default FormColumn